import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

const fmt = (v, currency = "INR") =>
  v == null ? "—" : new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(v));

// Operator-assignable kinds. Deliberately the same vocabulary the backend accepts — nothing invented here.
const KINDS = [
  { key: "GST", label: "GST" },
  { key: "CGST", label: "CGST" },
  { key: "SGST", label: "SGST" },
  { key: "IGST", label: "IGST" },
  { key: "VAT", label: "VAT" },
  { key: "TCS", label: "TCS" },
  { key: "DELIVERY_FREIGHT", label: "Delivery / Freight" },
  { key: "PACKING", label: "Packing" },
  { key: "HANDLING", label: "Handling" },
  { key: "OTHER_CHARGE", label: "Other charge" },
  { key: "DISCOUNT", label: "Discount" },
  { key: "ROUND_OFF", label: "Round off" },
];

const UnclassifiedRow = ({ charge, currency, canEdit, busy, onClassify }) => {
  const [open, setOpen] = useState(false);
  return (
    <li className="rounded-md border border-amber-300 bg-amber-50 px-3 py-3" data-testid={`charge-unclassified-${charge.charge_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[14px] font-medium text-amber-900 flex items-center gap-1.5">
            <AlertTriangle size={14} aria-hidden />Unclassified charge
          </p>
          <p className="text-[13px] text-amber-900/80 truncate" title={charge.printed_label || ""}>
            Printed as “{charge.printed_label || "no label"}”
          </p>
        </div>
        <span className="text-[15px] font-semibold tabular-nums text-amber-900 shrink-0">{fmt(charge.amount, currency)}</span>
      </div>
      {canEdit && (
        open ? (
          <div className="mt-3 flex flex-wrap gap-2" data-testid={`charge-kind-options-${charge.charge_id}`}>
            {KINDS.map((k) => (
              <button key={k.key} type="button" disabled={busy} data-testid={`charge-kind-${charge.charge_id}-${k.key}`}
                onClick={() => onClassify(charge.charge_id, k.key)}
                className="min-h-[40px] px-3 rounded-full border border-amber-400 bg-white text-[13px] text-amber-900 disabled:opacity-60 hover:bg-amber-100 transition-colors">
                {k.label}
              </button>
            ))}
          </div>
        ) : (
          <button type="button" onClick={() => setOpen(true)} data-testid={`charge-classify-${charge.charge_id}`}
            className="mt-3 min-h-[40px] px-3 rounded-md border border-amber-400 bg-white text-[13px] font-medium text-amber-900 inline-flex items-center gap-2">
            {busy && <Loader2 size={14} className="animate-spin" />}What is this charge?
          </button>
        )
      )}
    </li>
  );
};

/** Separated invoice components exactly as the supplier printed them: GST, VAT, TCS, delivery/freight and
 *  other charges each stand on their own. Only components actually present are shown — no zero rows. */
export const FinancialSummary = ({ fin, canEdit = false, busy = false, error = null, onClassify, testId = "bill-financials", totalTestId = null }) => {
  if (!fin || !(fin.rows || []).length) return null;
  const cur = fin.currency || "INR";
  const unclassified = fin.unclassified || [];
  const listed = fin.rows.filter((r) => r.kind !== "UNCLASSIFIED" && r.key !== "total");
  const total = fin.rows.find((r) => r.key === "total");
  const Line = ({ r, isTotal }) => (
    <div key={r.key} data-testid={`fin-row-${r.key}`}
      className={`flex items-baseline justify-between gap-4 py-2.5 ${isTotal ? "border-t border-[#E4E0D8] mt-1" : ""}`}>
      <dt className={`text-[14px] ${isTotal ? "font-semibold text-archival-primary" : "text-archival-secondary"}`}>
        {r.label}
        {r.note && <span className="block text-[12px] text-archival-muted">{r.note}</span>}
      </dt>
      <dd className={`tabular-nums shrink-0 ${isTotal ? "text-[17px] font-semibold text-archival-primary" : "text-[14px] text-archival-primary"}`}
        data-testid={isTotal && totalTestId ? totalTestId : `fin-amount-${r.key}`}>
        {fmt(r.amount, cur)}
      </dd>
    </div>
  );
  return (
    <section className="rounded-md border border-[#E4E0D8] bg-white" data-testid={testId}>
      <dl className="px-4 py-1">
        {listed.map((r) => <Line key={r.key} r={r} isTotal={false} />)}
        {!unclassified.length && total && <Line r={total} isTotal />}
      </dl>
      {unclassified.length > 0 && (
        <div className="px-3 pb-3 pt-1">
          <p className="text-[13px] text-archival-secondary pb-2" data-testid="fin-unclassified-note">
            {unclassified.length === 1 ? "One printed charge needs" : `${unclassified.length} printed charges need`} a decision before this bill can be confirmed.
          </p>
          <ul className="space-y-2">
            {unclassified.map((c) => (
              <UnclassifiedRow key={c.charge_id} charge={c} currency={cur} canEdit={canEdit} busy={busy} onClassify={onClassify} />
            ))}
          </ul>
          {error && <p role="alert" className="mt-2 text-[13px] text-red-800" data-testid="fin-classify-error">{error}</p>}
          {total && <dl className="px-1 pt-1"><Line r={total} isTotal /></dl>}
        </div>
      )}
      {fin.breakdown_availability === "NOT_AVAILABLE_FROM_HISTORICAL_SOURCE" && (
        <p className="px-4 pb-3 text-[12px] text-archival-muted" data-testid="fin-historical-note">
          This bill was captured before component-level tax capture, so only the printed total tax is on record.
        </p>
      )}
    </section>
  );
};
